import { USBDevice } from '../global';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPrinterList } from '@/utils/printer';

interface UsbDeviceChangeListener {
  (deviceList: USBDevice[]): void;
}

let id = 0;
let usbDeviceChangeListeners = new Map<number, UsbDeviceChangeListener>();
export const addUsbDeviceChangeListener = (listener: UsbDeviceChangeListener) => {
  id++;
  usbDeviceChangeListeners.set(id, listener);
  return id;
};

export const removeUsbDeviceChangeListener = (key: number) => {
  usbDeviceChangeListeners.delete(key);
};

// 设备列表
let usbDeviceList: USBDevice[];
try {
  usbDeviceList = (JSON.parse(window.__POS_Device__.getConnectedUsbDevices()) as USBDevice[]) || [];
} catch (e) {
  usbDeviceList = [];
}

// 宿主调用
window.__ON_CONNECTED_USB_CHANGE__ = newDeviceList => {
  if (newDeviceList) {
    usbDeviceList = newDeviceList;
    usbDeviceChangeListeners.forEach(listener => {
      listener(newDeviceList);
    });
  }
};

/**
 * 指定 设备号 查找设备是否连接
 */
export const isUsbDeviceConnected = (
  vendorId: number,
  productId: number,
  specifiedUsbDeviceList?: USBDevice[]
) => {
  const deviceList = specifiedUsbDeviceList || usbDeviceList;
  return (
    deviceList.findIndex(device => {
      return device.mProductId === productId && device.mVendorId === vendorId;
    }) > -1
  );
};

/**
 * 指定 设备 格式: vendorId-productId 查找设备是否连接
 */
export const isUsbDeviceConnectedByStr = (str: string, specifiedUsbDeviceList?: USBDevice[]) => {
  try {
    const split = str.split('-');
    return isUsbDeviceConnected(+split[0], +split[1], specifiedUsbDeviceList);
  } catch (e) {
    return false;
  }
};

export const connectUsbById = (id: string) => {
  try {
    const split = id.split('-');
    if (split.length === 2 && split[0] && split[1]) {
      window.__POS_Device__.connectUsb(+split[0], +split[1]);
    }
  } catch (e) {
    return false;
  }
};

export const getUsbDeviceName = (device: USBDevice) => {
  let name = ``;
  name += device.mManufacturerName ? device.mManufacturerName : '';
  name += device.mProductName ? ` ${device.mProductName}` : '';
  name = name.trim().length ? name : `未命名设备(${device.mVendorId}-${device.mProductId})`;

  return name;
};

/**
 * 设备连接列表 hooks
 */
export const useUsbDeviceList = () => {
  const [deviceList, setDeviceList] = useState(usbDeviceList);

  useEffect(() => {
    const key = addUsbDeviceChangeListener(newList => {
      setDeviceList(newList);
    });
    return () => {
      removeUsbDeviceChangeListener(key);
    };
  }, []);

  return [deviceList];
};

interface UsbDevicesMap {
  [key: string]: {
    displayName: string;
    connected: boolean;
    device: USBDevice;
  };
}

/**
 * 设备 map 带连接状态
 */
export const useUsbDeviceMap = () => {
  const [usbDevices, setUsbDevices] = useState<USBDevice[]>([]);

  const updateDevices = useCallback(() => {
    setUsbDevices(getPrinterList());
  }, [setUsbDevices]);

  const devicesMap = useMemo(() => {
    return usbDevices.reduce((acc, cur) => {
      acc[`${cur.mVendorId}-${cur.mProductId}`] = {
        connected: isUsbDeviceConnected(cur.mVendorId, cur.mProductId),
        displayName: getUsbDeviceName(cur),
        device: cur,
      };
      return acc;
    }, {} as UsbDevicesMap);
  }, [usbDevices]);

  useEffect(() => {
    updateDevices();
    // 监听拔插情况
    const key = addUsbDeviceChangeListener(() => {
      updateDevices();
    });
    return () => {
      removeUsbDeviceChangeListener(key);
    };
  }, []);

  return [devicesMap, updateDevices] as const;
};
